import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Typography,
  Divider,
  Button,
  Switch,
  TextField,
  Stack,
  FormControlLabel,
} from "@mui/material";
import Select from "components/Select";
import { Client } from "api/models/Client";
import {
  addClientToGOGroup,
  getClientGOGroup,
  removeClientFromGOGroup,
  saveClientGOSettings,
  updateClient,
} from "api/resources/clients";
import {
  getClientGroups,
  getClientGroupsByClientId,
  addClientToClientGroup,
  removeClientFromClientGroup,
} from "api/resources/clientGroups";
import { ClientGroup } from "api/models/ClientGroup";
import { Snackbar } from "types/snackbar";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectClientGOSettings, selectGOGroups } from "store/selectors";
import { fetchClientGOSettings, fetchGOGroups } from "store/appSlice";
import ServiceFees from "./ServiceFees";

interface SettingsProps {
  client: Client;
  setSnackbar: React.Dispatch<React.SetStateAction<Snackbar>>;
  handleDispatchClient?: () => void;
}

interface GOFormValues {
  clientId: number;
  isEnabled: boolean;
  producerEecsCodeEe: string;
  traderEecsCodeEe: string;
  producerEecsCodeLv: string;
  traderEecsCodeLv: string;
  producerEecsCodeLt: string;
  traderEecsCodeLt: string;
}

const Settings: React.FC<SettingsProps> = ({
  client,
  setSnackbar,
  handleDispatchClient,
}: SettingsProps) => {
  const dispatch = useAppDispatch();
  const clientGOSettings = useAppSelector(selectClientGOSettings);
  const GOGroups = useAppSelector(selectGOGroups);
  const [contractType, setContractType] = useState<string>(client.contractType);
  const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
  const [selectedClientGroupIds, setSelectedClientGroupIds] = useState<
    number[]
  >([]);
  const [savedClientGroupIds, setSavedClientGroupIds] = useState<number[]>([]);
  const [clientGroups, setClientGroups] = useState<ClientGroup[]>([]);
  const [settingsChanged, setSettingsChanged] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [goFormValues, setGoFormValues] = useState<GOFormValues>({
    clientId: client.id,
    isEnabled: false,
    producerEecsCodeEe: "",
    traderEecsCodeEe: "",
    producerEecsCodeLv: "",
    traderEecsCodeLv: "",
    producerEecsCodeLt: "",
    traderEecsCodeLt: "",
  });

  useEffect(() => {
    dispatch(fetchClientGOSettings(client.id));
    dispatch(fetchGOGroups());

    getClientGroups()
      .then((response) => {
        setClientGroups(response.data);
      })
      .catch((error) => console.error("Error fetching client groups:", error));

    getClientGroupsByClientId(client.id)
      .then((response) => {
        const groupIds = (response.data ?? []).map(
          (group: ClientGroup) => group.id,
        );
        setSelectedClientGroupIds(groupIds);
        setSavedClientGroupIds(groupIds);
      })
      .catch((error) =>
        console.error("Error fetching the client's groups:", error),
      );
  }, [client]);

  useEffect(() => {
    if (clientGOSettings) {
      setGoFormValues(clientGOSettings);
    }
    if (client) {
      getClientGOGroup(client.id).then((result) => {
        setSelectedGroup(result.data.id);
      });
    }
  }, [client, clientGOSettings, GOGroups]);

  const handleChangeGoSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const goFormValuesCopy = { ...goFormValues };

    goFormValuesCopy.isEnabled = event.target.checked;

    setGoFormValues(goFormValuesCopy);
    setSettingsChanged(true);
  };

  const handleChangeGoValue = (value, name) => {
    const goFormValuesCopy = { ...goFormValues };

    goFormValuesCopy[name] = value;

    setGoFormValues(goFormValuesCopy);
    setSettingsChanged(true);
  };

  const handleSaveClient = () => {
    setLoading(true);

    const postData = {
      contractType: contractType,
    };

    updateClient(client.id, postData)
      .then(() => {
        setLoading(false);
        setSettingsChanged(false);
        handleDispatchClient();
        setSnackbar({
          open: true,
          severity: "success",
          message: "Changes saved",
        });
      })
      .catch(() => {
        setLoading(false);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Error saving changes",
        });
      });

    saveClientGOSettings(goFormValues).catch((error) => console.error(error));

    const groupData = {
      clientId: client.id,
      clientGroupId: selectedGroup,
    };

    if (selectedGroup) {
      addClientToGOGroup(groupData);
    } else {
      removeClientFromGOGroup(groupData);
    }

    const addedGroupIds = selectedClientGroupIds.filter(
      (id) => !savedClientGroupIds.includes(id),
    );
    const removedGroupIds = savedClientGroupIds.filter(
      (id) => !selectedClientGroupIds.includes(id),
    );
    Promise.all([
      ...addedGroupIds.map((id) => addClientToClientGroup(id, client.id)),
      ...removedGroupIds.map((id) =>
        removeClientFromClientGroup(id, client.id),
      ),
    ])
      .then(() => setSavedClientGroupIds(selectedClientGroupIds))
      .catch((error) =>
        console.error("Error saving the client's groups:", error),
      );
  };

  const options = [
    {
      label: "Total",
      value: "TOTAL",
    },
    {
      label: "Fixed",
      value: "FIXED",
    },
    {
      label: "External",
      value: "EXTERNAL",
    },
  ];

  return (
    <>
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: "2rem",
          }}
        >
          <Typography variant="h5" component="h5">
            Settings
          </Typography>
        </Box>
        <Box sx={{ mb: "2rem", maxWidth: "1200px" }}>
          <Divider textAlign="left" sx={{ mb: "1rem" }}>
            General
          </Divider>
          <Box sx={{ mb: 3 }}>
            <Select
              label="Contract type"
              id="contractType"
              name="contractType"
              value={contractType}
              onChange={(e) => {
                setContractType(e.target.value);
                setSettingsChanged(true);
              }}
              minWidth={400}
              maxWidth={400}
              options={options}
            />
          </Box>
          <Box sx={{ mb: 6 }}>
            <Autocomplete
              multiple
              size="small"
              id="clientGroups"
              options={clientGroups}
              value={clientGroups.filter((group) =>
                selectedClientGroupIds.includes(group.id),
              )}
              getOptionLabel={(group) => group.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_event, groups) => {
                setSelectedClientGroupIds(groups.map((group) => group.id));
                setSettingsChanged(true);
              }}
              sx={{ maxWidth: 400, background: "white" }}
              renderInput={(params) => (
                <TextField {...params} label="Client groups" />
              )}
            />
          </Box>
          <Divider textAlign="left" sx={{ mb: "1rem" }}>
            Service fees
          </Divider>
          <Box sx={{ mb: 6 }}>
            <ServiceFees
              clientId={client.id}
              clientContractType={client.contractType}
            />
          </Box>
          <Divider textAlign="left" sx={{ mb: "1rem" }}>
            Guarantees of Origin (GO)
          </Divider>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              mt: 3,
              mb: 6,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={goFormValues.isEnabled}
                  onChange={handleChangeGoSwitch}
                />
              }
              label="Sync is enabled"
            />
            <Box sx={{ mt: 3 }} display="flex" gap={3}>
              <Box>
                <Typography component="p" fontWeight={500}>
                  Estonia:
                </Typography>
                <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
                  <TextField
                    id="ee_producer_code"
                    name="ee_producer_code"
                    label="Producer EECS code"
                    size="small"
                    value={goFormValues.producerEecsCodeEe || ""}
                    onChange={(e) =>
                      handleChangeGoValue(e.target.value, "producerEecsCodeEe")
                    }
                  />
                  <TextField
                    id="ee_trader_code"
                    name="ee_trader_code"
                    label="Trader EECS code"
                    size="small"
                    value={goFormValues.traderEecsCodeEe || ""}
                    onChange={(e) =>
                      handleChangeGoValue(e.target.value, "traderEecsCodeEe")
                    }
                  />
                </Stack>
              </Box>
              <Box>
                <Typography component="p" fontWeight={500}>
                  Latvia:
                </Typography>
                <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
                  <TextField
                    id="lv_producer_code"
                    name="lv_producer_code"
                    label="Producer EECS code"
                    size="small"
                    value={goFormValues.producerEecsCodeLv || ""}
                    onChange={(e) =>
                      handleChangeGoValue(e.target.value, "producerEecsCodeLv")
                    }
                  />
                  <TextField
                    id="lv_trader_code"
                    name="lv_trader_code"
                    label="Trader EECS code"
                    size="small"
                    value={goFormValues.traderEecsCodeLv || ""}
                    onChange={(e) =>
                      handleChangeGoValue(e.target.value, "traderEecsCodeLv")
                    }
                  />
                </Stack>
              </Box>
              <Box>
                <Typography component="p" fontWeight={500}>
                  Lithuania:
                </Typography>
                <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
                  <TextField
                    id="lt_producer_code"
                    name="lt_producer_code"
                    label="Producer EECS code"
                    size="small"
                    value={goFormValues.producerEecsCodeLt || ""}
                    onChange={(e) =>
                      handleChangeGoValue(e.target.value, "producerEecsCodeLt")
                    }
                  />
                  <TextField
                    id="lt_trader_code"
                    name="lt_trader_code"
                    label="Trader EECS code"
                    size="small"
                    value={goFormValues.traderEecsCodeLt || ""}
                    onChange={(e) =>
                      handleChangeGoValue(e.target.value, "traderEecsCodeLt")
                    }
                  />
                </Stack>
              </Box>
            </Box>
            <Box sx={{ mt: 4 }}>
              <Typography component="p" sx={{ mb: 2 }}>
                Assign group to client used in the summary report:
              </Typography>
              <Select
                label="GO group"
                id="go-group"
                name="go-group"
                minWidth={400}
                value={selectedGroup || ""}
                onChange={(e) => {
                  setSelectedGroup(e.target.value);
                  setSettingsChanged(true);
                }}
                options={[
                  { label: "-----", value: null },
                  ...GOGroups.map((group) => {
                    return {
                      label: group.name,
                      value: group.id,
                    };
                  }),
                ]}
              />
            </Box>
          </Box>
        </Box>
        {settingsChanged && (
          <Button
            variant="contained"
            onClick={handleSaveClient}
            disabled={loading}
          >
            Save changes
          </Button>
        )}
      </Box>
    </>
  );
};

export default Settings;
